import type {
  LiveRankingExplainer,
  LiveRankingPolicy,
} from '../LivePreview/liveViewModel';
import type { WireRankingPolicy } from './liveWireContract';

/*
 * THE ONE PLACE the console turns a ranking version into words. The policy page, the job modal's
 * "How this list is ordered" legend and every worker's "why" read the version from the wire — the
 * version the active policy PINS, or the version a recorded run USED — and never assume one.
 *
 * The version is told apart by its LEAD (what it ranks on before the tier-first order), not by its
 * number, so a run explains itself by what actually ranked it. Points below are graphy's
 * Ranking::Vocabulary values (ACTIVITY_SEEN_POINTS, ACTIVITY_WORKED_POINTS, ACTIVITY_SAID_YES_POINTS);
 * the wire carries only keys and recorded facts, never prose.
 */

export const toRankingPolicy = (
  r: WireRankingPolicy | null | undefined,
): LiveRankingPolicy | null =>
  r
    ? { versionNumber: r.version_number, label: r.label, lead: [...r.lead] }
    : null;

export const ranksOnActivity = (r: LiveRankingPolicy | null): boolean =>
  r !== null && r.lead.includes('activity_points');

export const ranksMonitoredLast = (r: LiveRankingPolicy | null): boolean =>
  r !== null && r.lead.includes('monitored_guard');

// "version 3 (active first)" — the stored label without its machine prefix and revision suffix
// (fr-active_first-r3 -> "active first"). A run bound to no version says exactly that.
const plainLabel = (label: string): string =>
  label
    .replace(/^fr-/, '')
    .replace(/-r\d+$/, '')
    .replace(/_/g, ' ');

export const rankingVersionLabel = (r: LiveRankingPolicy | null): string =>
  r === null
    ? 'not recorded on this run'
    : `version ${r.versionNumber} (${plainLabel(r.label)})`;

// Version two: tier first, with the score and exact miles as tie-breaks.
export const VERSION_TWO_EXPLAINER: LiveRankingExplainer = {
  intro:
    'Candidates are ranked by ranking version 2: dependability tier first, then distance band, then relationship, then the higher dependability score, then the closer distance, with worker id only as the last resort.',
  steps: [
    'Dependability tier — Platinum, Gold, Standard, then Monitored/Unknown.',
    'Distance band — closer bands rank first within the same tier.',
    'Relationship — a prior working relationship with this client breaks remaining ties.',
    'Higher dependability score — breaks ties inside the same tier, band and relationship; a missing score sorts last.',
    'Closer distance — exact miles, only when the score also ties.',
    'Worker id — a stable, arbitrary tie-break of last resort so ranking is always deterministic.',
  ],
  scoreNote:
    'The score breaks ties inside a tier and distance band; it never outranks a tier or a band.',
  unknownNote:
    'A worker with unknown dependability sorts after every scored tier, never before.',
};

// Version one: the same tier-first order with worker id as the only tie-break.
export const VERSION_ONE_EXPLAINER: LiveRankingExplainer = {
  intro:
    'Candidates are ranked by ranking version 1: dependability tier first, then distance band, then relationship, with worker id as the tie-break.',
  steps: [
    'Dependability tier — Platinum, Gold, Standard, then Monitored/Unknown.',
    'Distance band — closer bands rank first within the same tier.',
    'Relationship — a prior working relationship with this client breaks remaining ties.',
    'Worker id — a stable tie-break so ranking is always deterministic.',
  ],
  scoreNote: 'Version 1 does not use the dependability score to order workers.',
  unknownNote:
    'A worker with unknown dependability sorts after every scored tier, never before.',
};

// A run that recorded no ranking version at all. None exist in production (checked 23 Oct 2026);
// the wording stays generic because such a run may predate the versioned comparator, whose
// tie-breaks differed.
export const UNVERSIONED_EXPLAINER: LiveRankingExplainer = {
  intro:
    'This run recorded no ranking version. It was ranked tier first: dependability tier, then distance band, then relationship.',
  steps: [
    'Dependability tier — Platinum, Gold, Standard, then Monitored/Unknown.',
    'Distance band — closer bands rank first within the same tier.',
    'Relationship — a prior working relationship with this client breaks remaining ties.',
  ],
  scoreNote: 'Remaining ties were broken by the system.',
  unknownNote:
    'A worker with unknown dependability sorts after every scored tier, never before.',
};

// Plain words for Ops. "Group" is the tiered group vs newer workers with no tier yet: the two are
// ranked separately, so "last" for a monitored worker means last among tiered workers.
export const VERSION_THREE_STEPS: readonly string[] = [
  'Same checks as before: a worker who fails one is not on the list.',
  'Monitored workers go last among tiered workers, even if they use the app a lot.',
  'Used the app in the last 24 hours: 3 points. Last 3 days: 2. Last 7 days: 1.',
  'Had a shift approved in the last 60 days: 2 points.',
  'Said yes to an invite in the last 60 days: 2 points.',
  'Most points first. Ties then follow the version 2 order: tier, distance, favorite or worked here before, score, closer miles.',
  'Still tied: a fixed shuffle for this run, then worker ID.',
];

export const VERSION_THREE_EXPLAINER: LiveRankingExplainer = {
  intro:
    'Version 3, "active first": workers with the most activity points go first, with Monitored workers placed after other tiered workers. Points include recent app use, approved shifts and accepted invitations. Ties follow the version 2 order.',
  steps: [...VERSION_THREE_STEPS],
  scoreNote:
    'Activity points go from 0 to 7. No activity means 0 points, and the worker stays on the list.',
  unknownNote:
    'A worker with no dependability history can show in the list but is never picked.',
};

// Built from the pinned number, so the note names whatever version carries this order.
export const activeFirstNote = (r: LiveRankingPolicy): string =>
  `Pinned: ranking version ${r.versionNumber}. We still limit how often one worker gets invited. This order can't be changed here; engineering ships a new version to change it.`;

export const rankingExplainerFor = (
  r: LiveRankingPolicy | null,
): LiveRankingExplainer => {
  if (ranksOnActivity(r)) return VERSION_THREE_EXPLAINER;
  if (r === null) return UNVERSIONED_EXPLAINER;
  if (r.versionNumber === 1) return VERSION_ONE_EXPLAINER;
  return VERSION_TWO_EXPLAINER;
};

type ActivityFacts = {
  activity_points: number | null;
  worked_60d: boolean | null;
  said_yes_60d: boolean | null;
};

// The app-use share of the points is what is left after work and yes, so it is named exactly
// without any clock arithmetic on the client.
const seenPhrase = (seenPoints: number): string => {
  if (seenPoints >= 3) return 'in the app within 24 hours';
  if (seenPoints === 2) return 'in the app within 3 days';
  if (seenPoints === 1) return 'in the app within 7 days';
  return 'not in the app in the last 7 days';
};

// "7 activity pts (in the app within 24 hours · shift approved in the last 60 days · said yes in the
// last 60 days)". Null points means the run did not record activity. Points with null work/yes facts
// means the activity read FAILED on that run (ActivitySignalsSource scores everyone 0, so the run
// ranked without activity points), which is said as such rather than read as a quiet worker.
export const activityPhrase = (f: ActivityFacts): string => {
  if (f.activity_points === null)
    return 'no activity saved for this run (0 points)';
  const pts = f.activity_points;
  if (f.worked_60d === null || f.said_yes_60d === null) {
    return "couldn't read app activity on this run, so nobody got activity points";
  }
  const head = `${pts} activity point${pts === 1 ? '' : 's'}`;
  const seen = pts - (f.worked_60d ? 2 : 0) - (f.said_yes_60d ? 2 : 0);
  const bits = [seenPhrase(seen)];
  if (f.worked_60d) bits.push('shift approved in the last 60 days');
  if (f.said_yes_60d) bits.push('said yes in the last 60 days');
  return `${head} (${bits.join(' · ')})`;
};

// Within the BAND only: the comparator sorts each band separately (reliability, then activation),
// so a monitored reliability worker can still sit above an activation-band worker. The tier itself
// is named later in the same line, so it is not repeated here.
export const MONITORED_LAST_PHRASE = 'placed after other tiered workers';
